import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  MenuItem,
  Paper,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Stack,
} from '@mui/material';
import { Person, Business, Phone, Save } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { profileService } from '../modules/profile/services/profileService';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';

interface ProfileFormData {
  phone: string;
  business_type: string;
  company_name?: string;
  address?: string;
  city?: string;
  state?: string;
}

const businessTypes = [
  { value: 'individual', label: 'Pessoa Física' },
  { value: 'farm_owner', label: 'Proprietário Rural' },
  { value: 'cooperative', label: 'Cooperativa' },
  { value: 'company', label: 'Empresa' },
  { value: 'consultant', label: 'Consultor' },
  { value: 'veterinarian', label: 'Veterinário' },
  { value: 'other', label: 'Outro' },
];

const steps = ['Informações Pessoais', 'Informações de Negócio', 'Finalizar'];

export const CompleteProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { authState, checkUserProfile } = useAuthContext();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<ProfileFormData>({
    phone: '',
    business_type: '',
    company_name: '',
    address: '',
    city: '',
    state: '',
  });

  const handleInputChange = (field: keyof ProfileFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleNext = () => {
    if (activeStep === 0 && !formData.phone) {
      setError('Telefone é obrigatório');
      return;
    }
    if (activeStep === 1 && !formData.business_type) {
      setError('Tipo de negócio é obrigatório');
      return;
    }
    setError(null);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!authState.user?.username) {
      setError('Usuário não autenticado');
      return;
    }

    setLoading(true);
    setError(null);

    try {
  
      
      const profileData = {
        username: authState.user.username,
        profile_type: 'individual',
        contact_details: {
          phone: formData.phone,
          email: authState.user.email,
          address: formData.address || '',
          city: formData.city || '',
          state: formData.state || '',
        },
        business_info: {
          business_type: formData.business_type,
          company_name: formData.company_name || '',
        },
        preferences: {
          language: authState.user.language_preference || 'pt',
          timezone: authState.user.timezone || 'America/Sao_Paulo',
        },
      };

  

      const result = await profileService.insert({ data: profileData });

      if (result.success) {
  
        
        // Atualizar status do perfil no contexto
        if (checkUserProfile) {
          await checkUserProfile(authState.user.username);
        }
        
        setSuccess('Perfil completado com sucesso!');
        
        // Redirecionar para dashboard após 2 segundos
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        throw new Error(result.error || 'Erro ao criar perfil');
      }
    } catch (err: any) {
      console.error('❌ [CompleteProfilePage] Erro ao criar perfil:', err);
      setError(err.message || 'Erro ao completar perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Stack spacing={3}>
              <TextField
                fullWidth
                label="Telefone *"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                placeholder="+55 11 99999-9999"
                InputProps={{
                  startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Cidade"
                  value={formData.city}
                  onChange={handleInputChange('city')}
                />
                <TextField
                  fullWidth
                  label="Estado"
                  value={formData.state}
                  onChange={handleInputChange('state')}
                />
              </Box>
              <TextField
                fullWidth
                label="Endereço"
                value={formData.address}
                onChange={handleInputChange('address')}
                multiline
                rows={2}
              />
            </Stack>
        );
      
      case 1:
        return (
          <Stack spacing={3}>
            <TextField
              fullWidth
              select
              label="Tipo de Negócio *"
              value={formData.business_type}
              onChange={handleInputChange('business_type')}
              InputProps={{
                startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            >
              {businessTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Nome da Empresa/Propriedade Rural"
              value={formData.company_name}
              onChange={handleInputChange('company_name')}
              placeholder="Nome do seu negócio (opcional)"
            />
          </Stack>
        );
      
      case 2:
        return (
          <Box textAlign="center">
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: '#22c55e',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Person sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h6" gutterBottom>
              Confirmar Informações
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Revise suas informações antes de finalizar:
            </Typography>
            <Paper sx={{ p: 2, textAlign: 'left', bgcolor: 'grey.50' }}>
              <Typography variant="body2"><strong>Nome:</strong> {authState.user?.full_name}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {authState.user?.email}</Typography>
              <Typography variant="body2"><strong>Telefone:</strong> {formData.phone}</Typography>
              <Typography variant="body2"><strong>Tipo de Negócio:</strong> {businessTypes.find(t => t.value === formData.business_type)?.label}</Typography>
              {formData.company_name && (
                <Typography variant="body2"><strong>Nome do Negócio:</strong> {formData.company_name}</Typography>
              )}
            </Paper>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Card sx={{ boxShadow: 3 }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box textAlign="center" mb={4}>
              <Typography variant="h4" gutterBottom color="primary">
                Complete seu Perfil
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Para continuar usando a plataforma, precisamos de algumas informações adicionais.
              </Typography>
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            {/* Step Content */}
            <Box mb={4}>
              {renderStepContent()}
            </Box>

            {/* Navigation Buttons */}
            <Box display="flex" justifyContent="space-between">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Voltar
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? undefined : <Save />}
                >
                  {loading ? 'Salvando...' : 'Finalizar Perfil'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                >
                  Próximo
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CompleteProfilePage;